import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'

import { RoutingPaths } from 'app/routes/routes.ts'

import { selectionStore } from 'entities/tour/models'

import { Tour } from 'shared/api/tours/search'
import EmptyImage from 'shared/images/empty-image.webp'
import { getCleanHTML } from 'shared/lib/getCleanHTML.ts'
import { priceShow } from 'shared/lib/priceShow.ts'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types.ts'
import { Button } from 'shared/ui/button'
import { Carousel } from 'shared/ui/carousel'

import s from './styles.module.scss'

interface Props {
  room: Tour
}

export const HotelListCard = ({ room }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const roomImages = useMemo(
    () =>
      room.room_info.rooms_images.map((i, index) => ({
        id: String(index),
        image: i.src,
        image_fullpath: i.src,
      })),
    [room.room_info.rooms_images],
  )

  const handleClickMore = () => {
    selectionStore.setSelectedRoom(room)
    navigate(RoutingPaths.tours.hotelRoomDetails.path())
  }

  return (
    <div className={s.card}>
      <Carousel
        dotsVariant="custom"
        images={roomImages}
        errorImage={EmptyImage}
        showCounter
      />

      <div className={s.info}>
        <h4 className={clsx(s.title, 'extra-bold-16')}>{room.room}</h4>

        <div className={s.description}>
          <div className={s.row}>
            <AppIcon
              id={AllIcons.DOUBLE_BED}
              width={16}
              height={16}
              className={s.icon}
            />

            <div>
              <span className={clsx(s.label, 'medium-12')}>
                {t('tours_block.room')}
              </span>
              <span
                className={clsx(s.label, s.labelSecondary, 'medium-12')}
                dangerouslySetInnerHTML={{
                  __html: getCleanHTML(room.room_info.descr, {
                    forbiddenTags: ['img'],
                  }),
                }}
              />
            </div>
          </div>
          <div className={s.row}>
            <AppIcon
              id={AllIcons.MEAL_TOUR}
              width={16}
              height={16}
              className={s.icon}
            />

            <span className={clsx(s.label, 'medium-12')}>
              {room.meal_human}
            </span>
          </div>
        </div>

        <div className={s.divider} />

        <div className={s.bottom}>
          <div className={s.priceBox}>
            <h6 className={clsx(s.price, 'extra-bold-16')}>
              от {priceShow(room.price)} &#8384;
            </h6>

            {room?.price_usd && (
              <p className={clsx('semi-bold-14', s.dollar)}>
                (~&#36;
                {priceShow(String(Math.ceil(Number(room?.price_usd || 0))))})
              </p>
            )}
          </div>

          <Button
            className={clsx(s.button, 'semi-bold-14')}
            onClick={handleClickMore}
          >
            {t('more')}
          </Button>
        </div>
      </div>
    </div>
  )
}
